import { consume } from '@lit/context';
import { localized, t } from '@weavedev/lit-i18next';
import { css, html, LitElement, nothing, PropertyValueMap } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { createRef, Ref, ref } from 'lit/directives/ref.js';

import {
  getShowPrintExampleForConfiguration,
  PrintExampleDto,
  PrintOptionPerPositionDto,
  SelectedPrintOptionDto,
  StepEnum,
} from '@/api-sdk';
import { FgFullscreenImage, FgPersonalisation } from '@/components';
import { iconChevron, iconStopwatch } from '@/components/icons';
import {
  ConfiguratorContextType,
  configuratorGlobal,
  global,
  GlobalComponentType,
  MobileContext,
  personalisation,
  PersonalisationType,
  printExample,
} from '@/context';
import {
  ANIMATION_SPEED,
  BREAKPOINT_LARGE_PX,
  BREAKPOINT_MEDIUM_PX,
} from '@/settings';
import {
  baseStyles,
  buttonStyles,
  formStyles,
  typographyStyles,
} from '@/styles';
import { UploadStatus } from '@/types';
import { getPersonalisationData } from '@/utils';

import '@/components/personalisation/wholesale/fg-personalisation-status-bar';
import '../../../fullscreen-image/fg-fullscreen-image';
import '../../../print-example/fg-print-example';
import '../../fg-configurator-step';
import './print-option/fg-print-option-color-count';
import './print-option/fg-print-option-select';

@customElement('fg-configurator-print-option-per-position')
@localized()
export class FgConfiguratorPrintOptionPerPosition extends LitElement {
  private fgPersonalisationDialogRef: Ref<FgPersonalisation> = createRef();

  @property({ type: Array })
  printPositions?: PrintOptionPerPositionDto[] | undefined;

  /**
   * printExampleData {@link PrintExampleDto} contains the print example data like uuid, filename, url
   * @property {PrintExampleDto} printExampleData
   */
  @property({ type: Object })
  printExampleData?: {
    configurationId: string;
    data: PrintExampleDto;
  };

  @property({ type: Array })
  showPrintOptions: { [key: number]: boolean | undefined } = {};

  @state()
  selectedPrintPosition: { [key: number]: boolean } = {};

  @state()
  selectedDescription: { [key: number]: string | undefined } = {};

  @state()
  currentSelectedPrintOption?: SelectedPrintOptionDto;

  @state()
  printExampleStatus!: UploadStatus;

  @state()
  firstRender = true;

  @consume({ context: global.context })
  globalContext!: GlobalComponentType;

  @consume({ context: configuratorGlobal.context, subscribe: true })
  configuratorContext!: ConfiguratorContextType;

  @consume({ context: configuratorGlobal.resetContext, subscribe: true })
  configuratorReset = false;

  @consume({ context: MobileContext, subscribe: true })
  isMobile: boolean = false;

  @consume({ context: personalisation.context, subscribe: true })
  personalisationContext: PersonalisationType[] = [];

  fullscreenImageDialogRefs: Ref<FgFullscreenImage>[] = [];

  /**
   * This handleSelectedPrintPosition is only for toggleing the green selected state of the selected print position
   */
  handleSelectedPrintPosition(
    index: number,
    selected: boolean,
    description?: string,
    code?: string,
  ) {
    this.selectedPrintPosition = {
      ...this.selectedPrintPosition,
      [index]: selected,
    };

    this.selectedDescription = {
      ...this.selectedDescription,
      [index]: description,
    };

    /**
     * If print option is changed, remove the personalisation data for the print option
     */
    if (code) {
      personalisation.providerRemoveEvent(this, code);
    }

    /**
     * If all selectedPrintPositions are false of there aren't any reset print example state.
     */
    if (
      Object.values(this.selectedPrintPosition).every(value => value === false)
    ) {
      printExample.updateProviderEvent(this, undefined);
      this.printExampleStatus = UploadStatus.INITIAL;
    }
  }

  openPersonalisationDialog(
    selectedPrintOption: SelectedPrintOptionDto | undefined | null,
  ) {
    if (!selectedPrintOption) {
      return;
    }
    this.currentSelectedPrintOption = selectedPrintOption;
    this.fgPersonalisationDialogRef.value?.openDialog();
  }

  togglePrintOptions(index: number) {
    this.showPrintOptions = {
      ...this.showPrintOptions,
      [index]: !this.showPrintOptions[index],
    };
  }

  protected willUpdate(
    _changedProperties: PropertyValueMap<FgConfiguratorPrintOptionPerPosition>,
  ) {
    if (_changedProperties.has('printPositions') && this.configuratorReset) {
      this.selectedDescription = {};
      this.selectedPrintPosition = {};
      this.showPrintOptions = {};
      configuratorGlobal.updateResetProviderUpdate(this, false);
    }
  }

  async firstUpdated() {
    const { configurationId } = this.configuratorContext;

    if (this.printPositions && configurationId) {
      await getPersonalisationData(this, configurationId);

      this.preFillPrintPosition();

      await this.preFillPrintExample();

      configuratorGlobal.updateGlobalProviderEvent(this, {
        ...this.configuratorContext,
        configurationId: '',
      });
    }
  }

  preFillPrintPosition() {
    if (this.configuratorContext.configurationId && this.printPositions) {
      this.printPositions.find((printPosition, index) =>
        printPosition.methods?.find(printPositionMethod => {
          if (printPositionMethod?.selected) {
            this.handleSelectedPrintPosition(
              index,
              true,
              printPositionMethod?.description,
            );
          }
        }),
      );
    }
  }

  async preFillPrintExample() {
    const { configurationId } = this.configuratorContext;

    if (!configurationId) {
      return;
    }

    const printExampleData = await getShowPrintExampleForConfiguration({
      configurationId: configurationId,
    });

    this.printExampleData = {
      configurationId: configurationId,
      data: printExampleData,
    };
  }

  render() {
    const { layout } = this.globalContext;
    const { salable, configurationId, productName } = this.configuratorContext;

    return html`
      <fg-configurator-step part=${layout}>
        <fg-step-header
          part=${layout}
          slot="step-header"
          .stepType=${StepEnum.print_option_per_position}
        ></fg-step-header>
        <div class="wrapper">
          ${this.printPositions?.map((printPosition, index) => {
            const {
              thumbnailImage,
              mainImage,
              name,
              colorCountOptions,
              cancelQueryString,
              selectedPrintOption,
              hspAvailable,
            } = printPosition;

            this.fullscreenImageDialogRefs[index] = createRef();

            const isMobileOpen =
              (this.isMobile && this.showPrintOptions[index]) || false;

            const showFullScreenImageIcon =
              !this.isMobile || (this.isMobile && this.showPrintOptions[index]);

            return html`
              <div
                class="print-position 
                ${classMap({
                  opened: isMobileOpen,
                  closed: !isMobileOpen,
                  selected: this.selectedPrintPosition[index],
                })}"
              >
                ${showFullScreenImageIcon
                  ? html`
                      <fg-fullscreen-image
                        class="fullscreen-button"
                        .imageUrl=${mainImage}
                        ${ref(this.fullscreenImageDialogRefs[index])}
                      ></fg-fullscreen-image>
                    `
                  : nothing}
                ${thumbnailImage
                  ? html`
                      <img
                        width="96px"
                        height="96px"
                        src=${thumbnailImage}
                        class="print-option-image"
                        @click=${(event: Event) => {
                          event.stopPropagation();

                          if (
                            (this.isMobile && this.showPrintOptions[index]) ||
                            !this.isMobile
                          ) {
                            this.fullscreenImageDialogRefs[index]?.value?.open(
                              event,
                            );
                          }
                        }}
                      />
                    `
                  : html`
                      <span class="spacer"></span>
                    `}

                <div class="print-methods-wrapper">
                  <h5 @click=${() => this.togglePrintOptions(index)}>
                    ${name} ${this.isMobile ? iconChevron : nothing}
                    ${this.isMobile && this.selectedDescription[index]
                      ? html`
                          <span>${this.selectedDescription[index]}</span>
                        `
                      : nothing}
                  </h5>

                  <div class="print-methods">
                    ${this.isMobile
                      ? html`
                          <select
                            @change=${(event: {
                              target: HTMLSelectElement;
                            }) => {
                              const selectedData = printPosition.methods.find(
                                obj => obj.queryString === event.target.value,
                              );
                              this.handleSelectedPrintPosition(
                                index,
                                true,
                                selectedData?.description,
                                selectedPrintOption?.code,
                              );
                              this.dispatchEvent(
                                new CustomEvent('option-selected', {
                                  composed: true,
                                  detail: {
                                    option: {
                                      queryString: event.target?.value,
                                    },
                                  },
                                }),
                              );

                              if (selectedData === undefined) {
                                if (
                                  selectedPrintOption &&
                                  selectedPrintOption.code
                                ) {
                                  personalisation.providerRemoveEvent(
                                    this,
                                    selectedPrintOption.code,
                                  );
                                }
                                this.handleSelectedPrintPosition(index, false);
                                this.dispatchEvent(
                                  new CustomEvent('option-selected', {
                                    composed: true,
                                    detail: {
                                      option: {
                                        queryString: cancelQueryString,
                                      },
                                    },
                                  }),
                                );
                              }
                            }}
                          >
                            <option
                              ?selected=${Object.keys(this.selectedDescription)
                                .length === 0}
                              value=${printPosition.cancelQueryString}
                            >
                              ${t('labels.noPrint')}
                            </option>
                            ${printPosition.methods?.map(
                              printPositionMethod => html`
                                <option
                                  ?selected=${this.selectedDescription[
                                    index
                                  ] === printPositionMethod.description}
                                  value="${printPositionMethod.queryString}"
                                >
                                  ${printPositionMethod.name} &dash;
                                  ${printPositionMethod.description}
                                  ${printPositionMethod.hspAvailable
                                    ? ` -  ${t('labels.24hProductionAvailable')}`
                                    : ''}
                                </option>
                              `,
                            )}
                          </select>
                        `
                      : html`
                          <fg-print-option-select
                            .methods=${printPosition.methods}
                            @option-selected=${(event: CustomEvent) => {
                              this.handleSelectedPrintPosition(
                                index,
                                true,
                                event.detail.description,
                                selectedPrintOption?.code,
                              );
                            }}
                            @no-print-selected=${() => {
                              if (
                                selectedPrintOption &&
                                selectedPrintOption.code
                              ) {
                                personalisation.providerRemoveEvent(
                                  this,
                                  selectedPrintOption.code,
                                );
                              }
                              this.handleSelectedPrintPosition(index, false);
                              this.dispatchEvent(
                                new CustomEvent('option-selected', {
                                  composed: true,
                                  detail: {
                                    option: {
                                      queryString: cancelQueryString,
                                    },
                                  },
                                }),
                              );
                            }}
                          ></fg-print-option-select>
                        `}
                  </div>
                  ${colorCountOptions &&
                  colorCountOptions?.length > 0 &&
                  !(!salable && layout == 'wholesale')
                    ? html`
                        <div class="print-colors">
                          <fg-print-option-color-count
                            .part=${layout}
                            .colorCount=${colorCountOptions}
                          ></fg-print-option-color-count>
                        </div>
                      `
                    : nothing}
                  ${this.isMobile && hspAvailable
                    ? html`
                        <span class="hsp-text">
                          <span>${iconStopwatch}</span>
                          ${t('labels.24hProductionAvailable')}
                        </span>
                      `
                    : nothing}
                </div>
                ${this.selectedPrintPosition[index] &&
                selectedPrintOption?.code &&
                selectedPrintOption &&
                !(!salable && layout == 'wholesale')
                  ? html`
                      <fg-personalisation-status-bar
                        part="wholesale"
                        .personalisationData=${personalisation.getDataWithCode(
                          this.personalisationContext,
                          selectedPrintOption?.code,
                        )}
                        .printOptionName=${name}
                        @open=${() =>
                          this.openPersonalisationDialog(selectedPrintOption)}
                        @remove=${() => {
                          personalisation.providerRemoveEvent(
                            this,
                            selectedPrintOption?.code,
                          );
                          this.requestUpdate();
                        }}
                      ></fg-personalisation-status-bar>
                    `
                  : nothing}
                ${!this.isMobile && hspAvailable
                  ? html`
                      <span class="hsp-text">
                        <span>${iconStopwatch}</span>
                        ${t('labels.24hProductionAvailable')}
                      </span>
                    `
                  : nothing}
              </div>
            `;
          })}

          <fg-personalisation
            ${ref(this.fgPersonalisationDialogRef)}
            .printOption=${this.currentSelectedPrintOption}
            .hasColorChoice=${this.currentSelectedPrintOption?.hasColorChoice}
            .isVisualDesignerAvailable=${Boolean(
              this.configuratorContext.visualDesigner
                ?.isVisualDesignerAvailable,
            )}
            @personalisation-completed=${() => this.requestUpdate()}
          >
            <fg-configuration-print-option-summary
              slot="print-option-summary"
              .option=${this.currentSelectedPrintOption}
            ></fg-configuration-print-option-summary>
          </fg-personalisation>
        </div>
      </fg-configurator-step>

      ${Object.values(this.selectedPrintPosition).includes(true) &&
      !(!salable && layout === 'wholesale')
        ? html`
            <fg-print-example
              .configurationId=${configurationId}
              .productName=${productName}
              .printExampleData=${this.printExampleData}
            ></fg-print-example>
          `
        : nothing}
    `;
  }

  static styles = [
    baseStyles,
    formStyles,
    buttonStyles,
    typographyStyles,
    css`
      .wrapper {
        display: flex;
        flex-direction: column;
        gap: 4px;
        margin-top: 16px;
      }

      .print-position {
        position: relative;
        background-color: var(--fg-gray--100, var(--fg-fallback-gray--100));
        border-radius: var(
          --fg-borderradius--sm,
          var(--fg-fallback-borderradius--sm)
        );
        border: 1px solid transparent;
        gap: 16px;
        padding: 8px;
        display: grid;
        gap: 16px;
        grid-template-columns: 48px 1fr;
        transition:
          background-color ease-in-out ${ANIMATION_SPEED}ms,
          border-color ease-in-out ${ANIMATION_SPEED}ms;
      }

      .print-position.selected,
      .print-position.opened.selected {
        background-color: var(--fg-success--light);
        border-color: var(--fg-success);
        color: #fff;
      }

      .print-position > img {
        aspect-ratio: 1 / 1;
        max-height: 48px;
        max-width: 48px;
        object-fit: contain;
        background-color: #fff;
        border-radius: var(
          --fg-borderradius--sm,
          var(--fg-fallback-borderradius--sm)
        );
      }

      .print-position .spacer {
        width: 48px;
        height: 48px;
        display: block;
      }

      .print-position.opened {
        background-color: #fff;
        box-shadow: var(--fg-boxshadow, var(--fg-fallback-boxshadow));
      }

      h5 {
        align-items: center;
        display: flex;
        column-gap: 16px;
        flex-wrap: wrap;
        justify-content: space-between;
        line-height: 28px;
        padding-top: 6px;
        padding-bottom: 8px;
        width: 100%;
      }

      .opened h5 {
        padding: 0;
      }

      h5 svg {
        transform: rotate(180deg);
        flex-shrink: 0;
      }

      .opened h5 svg {
        transform: rotate(0deg);
      }

      h5 span {
        color: var(--fg-gray--600, var(--fg-fallback-gray--600));
        font-size: 0.875rem;
        font-weight: 400;
        width: 100%;
      }

      .print-methods-wrapper {
        display: flex;
        flex-wrap: wrap;
        column-gap: 16px;
        justify-content: space-between;
      }

      .print-methods-wrapper h5 svg {
        color: var(--fg-secondary, var(--fg-fallback-secondary));
      }

      .print-methods-wrapper.closed {
        min-height: 48px;
      }

      .print-methods {
        align-items: stretch;
        display: none;
        min-width: 230px;
        width: 100%;
        padding-bottom: 8px;
      }

      .print-colors {
        margin-bottom: 8px;
        min-width: 150px;
        display: grid;
        flex-grow: 1;
        height: 100%;
      }

      .opened .print-methods {
        display: flex;
        padding-top: 8px;
      }

      select {
        width: 100%;
        overflow-x: hidden;
      }

      .print-option-personalisation-details {
        color: var(--fg-success--dark, var(--fg-fallback-success--dark));
        display: flex;
        height: 48px;
        align-items: center;
        flex-grow: 1;
        justify-content: end;
      }

      .print-option-personalisation-details span {
        margin-right: 5px;
      }

      .print-option-personalisation-details button.remove {
        color: var(--fg-gray--700, var(--fg-fallback-gray--700));
        height: 24px;
        margin-left: 5px;
        width: 24px;
      }

      .hsp-text {
        color: var(--fg-gray--600, var(--fg-fallback-gray--600));
        grid-column-start: 2;
      }

      .fullscreen-button {
        position: absolute;
        top: 12px;
        left: 12px;
        color: var(--fg-secondary, var(--fg-fallback-secondary));
      }

      .opened .print-option-image {
        cursor: pointer;
      }

      fg-personalisation-status-bar {
        margin-top: 0.5rem;
        grid-column: 1 / 3;
      }

      @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
        h5 {
          display: block;
        }

        .opened h5 {
          height: 48px;
          width: 100%;
        }

        .print-option-image {
          cursor: pointer;
        }

        .print-methods {
          display: flex;
        }

        .opened .print-methods {
          height: 100%;
          padding-top: 0;
        }

        .fullscreen-button {
          visibility: hidden;
        }

        .print-position:hover .fullscreen-button {
          visibility: visible;
        }

        .print-position {
          align-items: center;
          grid-template-columns: 48px 1fr;
          padding: 8px;
        }

        .print-position .spacer,
        .print-position > img {
          width: 48px;
          height: 48px;
          margin-bottom: auto;
        }

        .print-colors {
          margin-bottom: 0;
        }

        .hsp-text svg {
          color: var(--fg-gray--900, var(--fg-fallback-gray--900));
        }
      }

      @media screen and (min-width: ${BREAKPOINT_LARGE_PX}px) {
        h5 {
          padding-top: 10px;
        }

        .print-position {
          grid-template-columns: 96px 1fr;
        }

        .print-position .spacer,
        .print-position > img {
          height: 96px;
          max-height: 96px;
          max-width: 96px;
          width: 96px;
        }

        .print-methods-wrapper {
          display: grid;
          grid-template-columns: 160px 1fr 1fr;
          flex-wrap: nowrap;
          justify-content: flex-start;
          align-items: center;
        }

        .print-methods {
          min-width: 250px;
          width: auto;
          padding-bottom: 0;
        }

        .print-position {
          row-gap: 0;
        }
      }
    `,
  ];
}
